import moment from 'moment';

/**
 * Search Console's inspection result, read into the rows of the four blocks.
 *
 * Every returned state keeps its raw value beside a readable one. The raw value is the evidence —
 * it is what Search Console's documentation and support threads use — and the readable value is
 * only a reading of it. A field Google did not return is "Not returned", never a blank and never a
 * zero or a failure, and an `*_UNSPECIFIED` value is "Unknown": Google answered without saying,
 * which is not a verdict either way. A value this page has no wording for yet is shown as it
 * arrived rather than hidden.
 */

const TIME_FORMAT = 'DD MMM YYYY, HH:mm';

/** The readable value of each state Google returns, and how it reads at a glance. */
const STATES = {
    verdict: {
        PASS: 'gain',
        PARTIAL: '',
        FAIL: 'loss',
        NEUTRAL: '',
    },
    robotsTxtState: {
        ALLOWED: 'gain',
        DISALLOWED: 'loss',
    },
    indexingState: {
        INDEXING_ALLOWED: 'gain',
        BLOCKED_BY_META_TAG: 'loss',
        BLOCKED_BY_HTTP_HEADER: 'loss',
        BLOCKED_BY_ROBOTS_TXT: 'loss',
    },
    pageFetchState: {
        SUCCESSFUL: 'gain',
        SOFT_404: 'loss',
        BLOCKED_ROBOTS_TXT: 'loss',
        NOT_FOUND: 'loss',
        ACCESS_DENIED: 'loss',
        SERVER_ERROR: 'loss',
        REDIRECT_ERROR: 'loss',
        ACCESS_FORBIDDEN: 'loss',
        BLOCKED_4XX: 'loss',
        INTERNAL_CRAWL_ERROR: 'loss',
        INVALID_URL: 'loss',
    },
    crawledAs: {
        DESKTOP: '',
        MOBILE: '',
    },
};

/** One returned enum, read: its wording, its tone, and whether Google actually answered. */
export function readState(t, field, value) {
    if (value === undefined || value === null || value === '') {
        return { reading: t('tools.gsc.not_returned'), tone: '', returned: false };
    }
    if (value.endsWith('_UNSPECIFIED')) {
        return { reading: t('tools.url_inspection.states.unknown'), tone: '', returned: true };
    }
    if (!(value in (STATES[field] ?? {}))) {
        return { reading: value, tone: '', returned: true };
    }

    return {
        reading: t(`tools.url_inspection.states.${field}.${value}`),
        tone: STATES[field][value],
        returned: true,
    };
}

export function formatTime(value) {
    return value ? moment(value).format(TIME_FORMAT) : null;
}

/** Block 01 — the request as it was sent, and when the answer came back. */
export function requestRows(t, inspection) {
    return [
        {
            id: 'url',
            field: t('tools.url_inspection.request.url'),
            value: inspection.inspection_url,
            isUrl: true,
        },
        {
            id: 'property',
            field: t('tools.url_inspection.request.property'),
            value: inspection.site_url,
        },
        {
            id: 'language',
            field: t('tools.url_inspection.request.language'),
            value: inspection.language_code,
        },
        {
            id: 'inspected_at',
            field: t('tools.url_inspection.request.inspected_at'),
            value: formatTime(inspection.inspected_at),
            raw: inspection.inspected_at,
        },
        {
            id: 'action',
            field: t('tools.url_inspection.request.action'),
            value: t('tools.url_inspection.request.action_value'),
        },
        {
            id: 'link',
            field: t('tools.url_inspection.request.link'),
            value: inspection.inspection_result?.inspectionResultLink ?? null,
            isUrl: true,
            linkLabel: t('tools.url_inspection.request.link_label'),
        },
    ];
}

/** Block 02 — Google's index status and crawl evidence, readable value beside the raw one. */
export function statusRows(t, inspection) {
    const index = inspection.inspection_result?.indexStatusResult ?? {};
    const state = (id, field) => ({
        id,
        field: t(`tools.url_inspection.status.${id}`),
        raw: index[field] ?? null,
        ...readState(t, field, index[field]),
    });

    return [
        state('verdict', 'verdict'),
        {
            // Google's own sentence, already readable; it is shown once as both.
            id: 'coverage',
            field: t('tools.url_inspection.status.coverage'),
            raw: index.coverageState ?? null,
            reading: index.coverageState ?? t('tools.gsc.not_returned'),
            tone: '',
            returned: !!index.coverageState,
        },
        state('robots', 'robotsTxtState'),
        state('indexing', 'indexingState'),
        state('fetch', 'pageFetchState'),
        {
            id: 'last_crawl',
            field: t('tools.url_inspection.status.last_crawl'),
            raw: index.lastCrawlTime ?? null,
            reading: formatTime(index.lastCrawlTime) ?? t('tools.gsc.not_returned'),
            tone: '',
            returned: !!index.lastCrawlTime,
        },
        state('crawled_as', 'crawledAs'),
    ];
}

/**
 * How the declared and the Google-selected canonical compare: exactly the same string, different
 * strings, or not comparable because Google did not return one of them. A plain string comparison
 * of what was returned — it says nothing about why they differ or about the rest of the site.
 */
export function canonicalMatch(inspection) {
    const index = inspection.inspection_result?.indexStatusResult ?? {};
    if (!index.userCanonical || !index.googleCanonical) return 'not_comparable';
    return index.userCanonical === index.googleCanonical ? 'same' : 'different';
}

/** Block 03 — the canonicals and the discovery references Google returned, one row each. */
export function canonicalRows(t, inspection) {
    const index = inspection.inspection_result?.indexStatusResult ?? {};
    const match = canonicalMatch(inspection);
    const flag = match === 'different' ? 'loss' : '';

    // A list Google returned empty or not at all is one "Not returned" row, so the reader sees the
    // field was asked about rather than a table that quietly has no sitemap rows.
    const listed = (id, values) =>
        values?.length
            ? values.map((value, index_) => ({
                  id: `${id}-${index_}`,
                  field: t(`tools.url_inspection.canonical.${id}`),
                  value,
                  isUrl: true,
                  tone: '',
              }))
            : [
                  {
                      id,
                      field: t(`tools.url_inspection.canonical.${id}`),
                      value: null,
                      tone: '',
                  },
              ];

    return [
        {
            id: 'inspected',
            field: t('tools.url_inspection.canonical.inspected'),
            value: inspection.inspection_url,
            isUrl: true,
            tone: '',
        },
        {
            id: 'user',
            field: t('tools.url_inspection.canonical.user'),
            value: index.userCanonical ?? null,
            isUrl: true,
            tone: flag,
        },
        {
            id: 'google',
            field: t('tools.url_inspection.canonical.google'),
            value: index.googleCanonical ?? null,
            isUrl: true,
            tone: flag,
        },
        {
            id: 'match',
            field: t('tools.url_inspection.canonical.match'),
            value: t(`tools.url_inspection.canonical.matches.${match}`),
            tone: flag,
        },
        ...listed('sitemap', index.sitemap),
        ...listed('referring', index.referringUrls),
    ];
}

/**
 * Block 04 — every detected rich-result item with each issue Google returned for it. An item with
 * no returned issue is one row saying so; the page does not audit structured data itself.
 */
export function richResultRows(t, inspection) {
    const detected = inspection.inspection_result?.richResultsResult?.detectedItems ?? [];
    const rows = [];

    detected.forEach((group, groupIndex) => {
        (group.items ?? []).forEach((item, itemIndex) => {
            const issues = item.issues ?? [];
            const base = {
                type: group.richResultType ?? t('tools.gsc.not_returned'),
                item: item.name ?? t('tools.gsc.not_returned'),
            };

            if (!issues.length) {
                rows.push({
                    ...base,
                    id: `${groupIndex}-${itemIndex}`,
                    issue: t('tools.url_inspection.rich.no_issue'),
                    severity: '',
                    tone: 'gain',
                });
                return;
            }

            issues.forEach((issue, issueIndex) => {
                rows.push({
                    ...base,
                    id: `${groupIndex}-${itemIndex}-${issueIndex}`,
                    issue: issue.issueMessage ?? t('tools.gsc.not_returned'),
                    severity: issue.severity ?? '',
                    tone: issue.severity === 'ERROR' ? 'loss' : '',
                });
            });
        });
    });

    return rows;
}
